import { createCrudModule } from "../crud";

export function packageListing(response) {
  const envelope = response?.data;
  const data = envelope?.packages ?? envelope;
  const rows = Array.isArray(data) ? data : data?.data || data?.items || [];
  if (!Array.isArray(rows)) throw new Error("The package list response is invalid.");
  return {
    rows,
    total: data?.total ?? response?.meta?.total ?? (Array.isArray(data) ? data.length : undefined),
    lastPage: data?.last_page ?? response?.meta?.last_page ?? 1,
    categories: response?.categories ?? envelope?.categories ?? data?.categories ?? response?.meta?.categories ?? [],
    categoryCounts: response?.category_counts ?? envelope?.category_counts ?? data?.category_counts ?? response?.meta?.category_counts ?? {},
    bookingsCount: envelope?.total_booking_count ?? response?.total_booking_count
      ?? response?.bookings_count ?? envelope?.bookings_count ?? data?.bookings_count ?? response?.meta?.bookings_count,
  };
}

const packagePath = ({ id }) => {
  if (id === undefined || id === null || id === "") throw new Error("A package id is required.");
  return `packages/${encodeURIComponent(id)}`;
};

const compact = values => Object.fromEntries(Object.entries(values).filter(([, value]) => value !== "" && value !== undefined && value !== null));
const weekdayNumber = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

const fileValue = value => value instanceof Blob ? value : value?.file instanceof Blob ? value.file : null;
const asArray = value => Array.isArray(value) ? value : value ? [value] : [];

const packageAttachments = values => {
  const result = [];
  const seen = new Set();
  const add = (item, kind, cta_text = "") => {
    const file = fileValue(item);
    if (!file || seen.has(file)) return;
    seen.add(file);
    result.push({ file, kind, cta_text });
  };
  asArray(values.thumbnail).forEach(item => add(item, "thumbnail"));
  asArray(values.images).forEach(item => add(item, "popup"));
  asArray(values.promotion?.images).forEach(item => add(item, "popup"));
  asArray(values.file).forEach(item => add(item, "brochure"));
  asArray(values.promotion?.file).forEach(item => add(item, "brochure"));
  asArray(values.brochures).forEach(item => asArray(item.files || item.file).forEach(file => add(file, "brochure", item.cta_text || "")));
  return result;
};

export function packageRequestBody(values = {}) {
  const body = { ...values };
  body.type = values.type === "informative" ? "promotion" : values.type;
  body.status = values.status === "published" ? "active" : values.status;
  if (values.category) body.category = values.category;
  else delete body.category;
  delete body.category_id;
  if (values.notification_email || values.admin_email) body.notification_email = values.notification_email || values.admin_email;
  if (values.terms_and_conditions || values.terms) body.terms_and_conditions = values.terms_and_conditions || values.terms;

  if (values.promotion) {
    body.promotion_settings = compact({
      title: values.promotion.name,
      price: values.promotion.price,
      description: values.promotion.description,
      external_url: values.promotion.content_type === "link" ? values.promotion.url : "",
    });
  }

  const bookingConfiguration = compact({
    campaign_start_date: values.campaign_start_date,
    campaign_end_date: values.campaign_end_date,
    capacity: values.daily_capacity === "" || values.daily_capacity == null ? undefined : Number(values.daily_capacity),
    capacity_scope: values.daily_capacity !== undefined ? "daily" : undefined,
    slot_duration_minutes: values.slot_duration === "" || values.slot_duration == null ? undefined : Number(values.slot_duration),
    available_days: Array.isArray(values.available_days)
      ? [...new Set(values.available_days.map(day => weekdayNumber[day] ?? Number(day)).filter(day => Number.isInteger(day) && day >= 0 && day <= 6))]
      : undefined,
  });
  if (Object.keys(bookingConfiguration).length) body.booking_configuration = bookingConfiguration;

  if (Array.isArray(values.registration_fields)) {
    body.content = { ...(body.content || {}), registration_fields: values.registration_fields };
  }
  if (values.content_type) {
    const displayTypes = { images: "popup_images", file: "file", link: "external_link" };
    body.content = compact({
      ...(body.content || {}),
      display_type: displayTypes[values.content_type] || values.content_type,
      external_url: values.content_type === "link" ? values.url : undefined,
    });
  }
  if (values.comparison) {
    body.content = { ...(body.content || {}), comparison_table: values.comparison };
  }
  if (Array.isArray(values.variations)) {
    body.variations = values.variations.map((variation, index) => compact({
      id: variation.id,
      name: variation.name,
      price: variation.price,
      promotional_price: variation.promotional_price,
      notification_email: variation.notification_email || variation.admin_email,
      is_active: variation.is_active ?? variation.status === "active",
      sort_order: variation.sort_order ?? index,
    }));
  }

  ["admin_email", "terms", "promotion", "campaign_start_date", "campaign_end_date",
    "daily_capacity", "slot_duration", "available_days", "registration_fields", "content_type", "url",
    "images", "thumbnail", "brochures", "file", "comparison", "note", "privilege", "id", "attachments", "remove_attachment_ids",
    "created_at", "updated_at", "deleted_at", "bookings_count"].forEach(key => delete body[key]);
  ["family_particulars", "consents", "declaration"].forEach(key => delete body[key]);
  return compact(body);
}

const appendFormValue = (form, key, value) => {
  if (value === undefined || value === null || value === "") return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => appendFormValue(form, `${key}[${index}]`, item));
  } else if (typeof value === "object" && !(value instanceof Blob)) {
    Object.entries(value).forEach(([child, item]) => appendFormValue(form, `${key}[${child}]`, item));
  } else {
    form.append(key, typeof value === "boolean" ? (value ? "1" : "0") : value);
  }
};

export function packageTransportBody(values = {}, update = false) {
  const attachments = packageAttachments(values);
  const removeIds = (values.remove_attachment_ids || []).filter(Boolean);
  const body = packageRequestBody(values);
  if (update) delete body.type;
  if (!attachments.length && !removeIds.length) return body;

  const form = new FormData();
  if (update) form.append("_method", "PUT");
  Object.entries(body).forEach(([key, value]) => appendFormValue(form, key, value));
  attachments.forEach(({ file, kind, cta_text }, index) => {
    form.append(`attachments[${index}][kind]`, kind);
    form.append(`attachments[${index}][file]`, file, file.name);
    if (cta_text) form.append(`attachments[${index}][cta_text]`, cta_text);
  });
  removeIds.forEach((id, index) => form.append(`remove_attachment_ids[${index}]`, id));
  return form;
}

const needsMultipart = values => packageAttachments(values).length > 0 || (values.remove_attachment_ids || []).some(Boolean);

const packagesModule = createCrudModule({
  name: "packages",
  stateKey: "Packages",
  endpoint: "packages",
  operations: {
    list: {
      query: ({ page = 1, length = 10, search = "", status = "", type = "", category = "" } = {}) => compact({
        paginate: page, size: length, search,
        status: status === "published" ? "active" : status === "archived" ? "inactive" : status,
        type: type === "informative" ? "promotion" : type,
        category,
      }),
      select: packageListing,
    },
    details: { path: packagePath },
    create: { path: "packages", body: values => packageTransportBody(values) },
    update: {
      path: packagePath,
      method: ({ data }) => needsMultipart(data) ? "POST" : "PUT",
      body: ({ data }) => packageTransportBody(data, true),
    },
    delete: { path: packagePath },
  },
});

export default packagesModule;
